import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { CreateClientRequest, IBankAccount } from "../services/crefisaService/createClient";
import { OfertaPreAprovada } from "../services/crefisaService/getOperations";
import { PartnerType } from "../utils/partners.enum";

export type CrefisaPartnerType =
  | PartnerType.CREFISA
  | PartnerType.CREFISA_INSS;

type SimulationData = {
  clientId: string;
  isRefinancing: boolean;
  borrowedValue: number;
  preApproveOffers?: OfertaPreAprovada[];
  type?: CrefisaPartnerType;
  naoRecebeViaCrefisa?: boolean;
  valorBeneficio?: number | "";
  possuiEmprestimo?: boolean | "";
  valorEmprestimoAtivo?: number | "";
  recebeViaCrefisa?: number | "";
  possuiDebitos?: boolean | "";
  valorTotalDebitos?: number | "";
};

type ProposalData = {
  value: number;
  prazo: number;
  simulationId?: string;
  refinancingId?: string;
  offerId?: string;
  ddd?: number;
  phone?: number;
  contract?: string;
};

interface CustomerData extends CreateClientRequest {
  customerId: string;
  id?: string;
}

interface CrefisaContextType {
  customerData: CustomerData | null;
  setCustomer: (customerData: Partial<CustomerData>) => void;
  consentBankAccount: IBankAccount | null;
  setConsentBankAccount: (bankAccount: IBankAccount | null) => void;
  proposalData: ProposalData | null;
  setProposal: (proposal: Partial<ProposalData>) => void;
  simulationData: SimulationData | null;
  setSimulation: (simulation: Partial<SimulationData>) => void;
  partnerType: CrefisaPartnerType;
  setPartnerType: (type: CrefisaPartnerType) => void;
}

const CrefisaContext = createContext<CrefisaContextType | undefined>(undefined);

export function CrefisaProvider({ children }: { children: ReactNode }) {
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [consentBankAccount, setConsentBankAccount] =
    useState<IBankAccount | null>(null);
  const [simulationData, setSimulationData] = useState<SimulationData | null>(
    null,
  );
  const [proposalData, setProposalData] = useState<ProposalData | null>(null);
  const [partnerType, setPartnerType] = useState<CrefisaPartnerType>(
    PartnerType.CREFISA,
  );

  const setCustomer = useCallback((data: Partial<CustomerData>) => {
    setCustomerData((prev) => ({ ...prev, ...data }) as CustomerData | null);
  }, []);

  const setProposal = useCallback((data: Partial<ProposalData>) => {
    setProposalData((prev) => ({ ...prev, ...data }) as ProposalData | null);
  }, []);

  const setSimulation = useCallback((data: Partial<SimulationData>) => {
    setSimulationData(
      (prev) => ({ ...prev, ...data }) as SimulationData | null,
    );
  }, []);

  const value = useMemo(
    () => ({
      customerData,
      setCustomer,
      consentBankAccount,
      setConsentBankAccount,
      proposalData,
      setProposal,
      simulationData,
      setSimulation,
      partnerType,
      setPartnerType,
    }),
    [
      customerData,
      setCustomer,
      consentBankAccount,
      setConsentBankAccount,
      proposalData,
      setProposal,
      simulationData,
      setSimulation,
      partnerType,
    ],
  );

  return (
    <CrefisaContext.Provider value={value}>
      {children}
    </CrefisaContext.Provider>
  );
}

export function useCrefisaContext() {
  const context = useContext(CrefisaContext);
  if (!context) {
    throw new Error("useCrefisaContext must be used within a CrefisaProvider");
  }
  return context;
}
